import React, { useState, useEffect, useCallback, useRef } from 'react';
import './Tour.css';

/* ───────────────────────────────────────────────────────────
   RESUME BUILDER TOUR STEPS
   ─────────────────────────────────────────────────────────── */
const RESUME_FEATURE_STEPS = [
  {
    id: 'section-nav',
    target: '.section-nav',
    title: 'Section Navigation',
    body: 'Your resume is split into <strong>7 tabs</strong>: Personal, Experience, Education, Skills, Misc, Job Match, and Page Setup. Click any tab to jump to that section — all your edits are preserved when you switch.',
    placement: 'right',
  },
  {
    id: 'form-content',
    target: '.form-content',
    title: 'Edit Your Content',
    body: 'Fill in your details here. Each section has its own <strong>formatting toolbar</strong> (click the \u25BC dropdown) to fine-tune fonts, spacing, and alignment without leaving the tab.',
    placement: 'right',
  },
  {
    id: 'ai-suggest',
    target: '.btn-ai-suggest',
    title: 'AI-Powered Suggestions',
    body: 'Click <strong>\u2728 Suggest</strong> (or <strong>\u2728 Improve</strong>) to get AI-written bullet points and summaries. You\u2019ll see Apply/Discard options — and you can always Undo if you change your mind.',
    placement: 'right',
  },
  {
    id: 'live-preview',
    target: '.preview-scroll',
    title: 'Live Preview',
    body: 'Your resume updates in <strong>real time</strong> as you type. This is exactly what your downloaded PDF will look like — no surprises.',
    placement: 'left',
  },
  {
    id: 'undo-redo',
    target: '.btn-undo-redo-group',
    title: 'Undo & Redo',
    body: 'Every change is tracked. <strong>Undo</strong> rolls back your last edit, and <strong>Redo</strong> brings it forward again. AI suggestions, skill additions, and style changes are all covered.',
    placement: 'bottom',
  },
  {
    id: 'cover-letter',
    target: '.btn-go-to-cl',
    title: 'Cover Letter',
    body: 'Jump straight to your <strong>cover letter builder</strong>. It pulls your personal info from the resume automatically so you don\u2019t have to re-enter anything.',
    placement: 'bottom',
  },
  {
    id: 'compare-template',
    target: '.btn-compare-template',
    title: 'Compare Templates',
    body: 'Preview your resume in a different template <strong>side-by-side</strong> with your current one. Your content stays exactly the same — only the design changes.',
    placement: 'bottom',
  },
  {
    id: 'change-template',
    target: '.btn-change-template',
    title: 'Change Template',
    body: 'Browse the full template gallery and switch designs. All your content carries over — the template only affects layout and styling.',
    placement: 'bottom',
  },
  {
    id: 'download',
    target: '.btn-download',
    title: 'Download PDF',
    body: 'When you\u2019re happy with your resume, click here to download a <strong>pixel-perfect PDF</strong> ready to send to employers.',
    placement: 'bottom',
  },
];

export function getResumeTourSteps(mode) {
  const welcome = {
    id: 'welcome',
    target: null,
    title: 'Welcome to Cedar Resumes',
    body: mode === 'dashboard'
      ? 'We\u2019ve loaded a <strong>sample resume</strong> so you can see the editor in action. Let\u2019s take a quick tour of how everything works.'
      : 'Let\u2019s take a quick tour of the editor so you know where everything is. Your resume is untouched \u2014 this is just a walkthrough. You can restart this tour anytime from the <strong>?</strong> button.',
  };

  if (mode === 'dashboard') {
    return [welcome, ...RESUME_FEATURE_STEPS, ...DASHBOARD_CL_STEPS, ...DASHBOARD_PROJECT_STEPS];
  }
  return [welcome, ...RESUME_FEATURE_STEPS, EDITOR_FEEDBACK_STEP];
}

const EDITOR_FEEDBACK_STEP = {
  id: 'editor-feedback',
  target: null,
  title: 'You\u2019re all set!',
  body: 'That\u2019s the full editor. Have thoughts on Cedar Resumes? We\u2019d love your <strong>feedback</strong> \u2014 use the tab on the right edge anytime.',
};

// Index where cover letter steps begin in the dashboard tour
export const DASHBOARD_CL_START = 1 + RESUME_FEATURE_STEPS.length; // welcome + resume steps

const DASHBOARD_CL_STEPS = [
  {
    id: 'cl-transition',
    target: null,
    title: 'Cover Letter Builder',
    body: 'Now let\u2019s check out the <strong>Cover Letter builder</strong>. This cover letter is linked to your resume \u2014 your personal info carries over automatically and the template styling matches.',
  },
  {
    id: 'cl-details',
    target: '.cl-details-toolbar',
    title: 'Letter Details',
    body: 'Set the <strong>recipient, company, role, and date</strong> here. Fields linked to your resume are locked \u2014 edit them in Personal Info instead.',
    placement: 'right',
  },
  {
    id: 'cl-generate',
    target: '.btn-cl-generate-all',
    title: 'Generate Full Letter',
    body: 'Click here to generate a complete cover letter using AI. Each section can also be regenerated individually.',
    placement: 'right',
  },
  {
    id: 'cl-sections',
    target: '.cl-section-card',
    title: 'Section Cards',
    body: 'Each card is one section of your letter. Click the <strong>text to edit</strong>, or use the regenerate button to get a new AI draft for just that section.',
    placement: 'right',
  },
  {
    id: 'cl-preview',
    target: '.cl-preview-scroll',
    title: 'Live Preview',
    body: 'Just like the resume editor, your cover letter updates <strong>in real time</strong> and the preview matches your downloaded PDF exactly.',
    placement: 'left',
  },
  {
    id: 'cl-download',
    target: '.btn-cl-download',
    title: 'Download PDF',
    body: 'Download your finished cover letter as a <strong>PDF</strong>. It uses the same styling as your paired resume template for a cohesive application package.',
    placement: 'bottom',
  },
];

const DASHBOARD_PROJECT_STEPS = [
  {
    id: 'my-project-btn',
    target: '.btn-cl-back',
    title: 'Back to My Project',
    body: 'Click <strong>My Project</strong> to return to your project hub, where all your documents live in one place.',
    placement: 'bottom',
  },
  {
    id: 'project-home',
    target: '.proj-home-docs',
    title: 'Your Project Hub',
    body: 'This is your project page. Your <strong>resume and cover letters</strong> are organized here \u2014 edit, duplicate, or add new documents anytime. You can create up to 3 cover letters per project.',
    placement: 'bottom',
  },
  {
    id: 'dashboard-feedback',
    target: null,
    title: 'You\u2019re all set!',
    body: 'That\u2019s the full Cedar Resumes experience. Have thoughts or suggestions? We\u2019d love your <strong>feedback</strong> \u2014 use the tab on the right edge anytime.',
  },
];

// Index where project-home steps begin in the dashboard tour
// my-project-btn is still on cl-builder; project-home and feedback are on project-home
export const DASHBOARD_PROJECT_START = DASHBOARD_CL_START + DASHBOARD_CL_STEPS.length + 1;

/* ───────────────────────────────────────────────────────────
   COVER LETTER BUILDER TOUR STEPS (standalone, from ? button)
   ─────────────────────────────────────────────────────────── */
export const COVER_LETTER_TOUR_STEPS = [
  {
    id: 'cl-welcome',
    target: null,
    title: 'Cover Letter Builder',
    body: 'This is where you craft a cover letter that pairs with your resume. Let\u2019s walk through the key features.',
  },
  {
    id: 'cl-details',
    target: '.cl-details-toolbar',
    title: 'Letter Details',
    body: 'Open this dropdown to set the <strong>recipient, company, role, and date</strong>. Fields linked to your resume are locked — edit them in Personal Info instead.',
    placement: 'right',
  },
  {
    id: 'cl-generate',
    target: '.btn-cl-generate-all',
    title: 'Generate Full Letter',
    body: 'Click here to generate a complete cover letter using AI. Each section (greeting, intro, body, closing) can also be regenerated individually.',
    placement: 'right',
  },
  {
    id: 'cl-sections',
    target: '.cl-section-card',
    title: 'Section Cards',
    body: 'Each card is one section of your letter. Click the <strong>text to edit</strong>, or use the regenerate button to get a new AI draft for just that section.',
    placement: 'right',
  },
  {
    id: 'cl-preview',
    target: '.cl-preview-scroll',
    title: 'Live Preview',
    body: 'Just like the resume editor, your cover letter updates <strong>in real time</strong> and the preview matches your downloaded PDF exactly.',
    placement: 'left',
  },
  {
    id: 'cl-download',
    target: '.btn-cl-download',
    title: 'Download PDF',
    body: 'Download your finished cover letter as a <strong>PDF</strong>. It uses the same styling as your paired resume template for a cohesive application package.',
    placement: 'bottom',
  },
  {
    id: 'cl-feedback',
    target: null,
    title: 'You\u2019re all set!',
    body: 'That\u2019s the cover letter builder. Have thoughts on Cedar Resumes? We\u2019d love your <strong>feedback</strong> \u2014 use the tab on the right edge anytime.',
  },
];

/* ───────────────────────────────────────────────────────────
   TOUR COMPONENT
   ─────────────────────────────────────────────────────────── */
const SPOTLIGHT_PAD = 8;

export default function Tour({ steps, onClose, onStepChange, currentStep }) {
  const [internalStep, setInternalStep] = useState(0);
  const controlled = currentStep !== undefined;
  const current = controlled ? currentStep : internalStep;
  const goTo = controlled
    ? (n) => { if (onStepChange) onStepChange(typeof n === 'function' ? n(current) : n); }
    : (n) => { const next = typeof n === 'function' ? n(internalStep) : n; setInternalStep(next); if (onStepChange) onStepChange(next); };

  const [spotlightRect, setSpotlightRect] = useState(null);
  const [tooltipPos, setTooltipPos] = useState(null);
  const [ready, setReady] = useState(false);
  const tooltipRef = useRef(null);
  const resizeTimer = useRef(null);
  const measureTimer = useRef(null);
  const readyRef = useRef(false);

  const step = steps[current];
  const isFirst = current === 0;
  const isLast = current === steps.length - 1;

  // ── Center tooltip in viewport (pixel-based) ──────────
  const centerTooltip = useCallback(() => {
    if (!tooltipRef.current) return;
    const tt = tooltipRef.current.getBoundingClientRect();
    setTooltipPos({
      top: (window.innerHeight - tt.height) / 2,
      left: (window.innerWidth - tt.width) / 2,
      placement: null,
    });
    if (!readyRef.current) {
      readyRef.current = true;
      setTimeout(() => setReady(true), 20);
    }
  }, []);

  // ── Measure target element and position tooltip ───────
  const measure = useCallback(() => {
    if (!step) return;
    if (!step.target) {
      setSpotlightRect(null);
      requestAnimationFrame(centerTooltip);
      return;
    }

    const el = document.querySelector(step.target);
    if (!el) {
      setSpotlightRect(null);
      requestAnimationFrame(centerTooltip);
      return;
    }

    // Scroll element into view — instant on mobile to avoid nested scroll timing issues
    const isMobile = window.innerWidth <= 600;
    el.scrollIntoView({ behavior: isMobile ? 'instant' : 'smooth', block: 'nearest', inline: 'nearest' });

    const positionTooltip = () => {
      const rect = el.getBoundingClientRect();
      const sr = {
        top: rect.top - SPOTLIGHT_PAD,
        left: rect.left - SPOTLIGHT_PAD,
        width: rect.width + SPOTLIGHT_PAD * 2,
        height: rect.height + SPOTLIGHT_PAD * 2,
      };
      setSpotlightRect(sr);

      // Position tooltip
      if (!tooltipRef.current) return;
      const tt = tooltipRef.current.getBoundingClientRect();
      const isMobile = window.innerWidth <= 600;
      let placement = step.placement || 'bottom';
      if (isMobile) {
        // On mobile, always pick top or bottom based on where the target is on screen
        placement = (sr.top + sr.height / 2) > window.innerHeight * 0.4 ? 'top' : 'bottom';
      }
      let top, left;
      const gap = 14;

      switch (placement) {
        case 'bottom':
          top = sr.top + sr.height + gap;
          left = sr.left + sr.width / 2 - tt.width / 2;
          break;
        case 'top':
          top = sr.top - tt.height - gap;
          left = sr.left + sr.width / 2 - tt.width / 2;
          break;
        case 'left':
          top = sr.top + sr.height / 2 - tt.height / 2;
          left = sr.left - tt.width - gap;
          break;
        case 'right':
          top = sr.top + sr.height / 2 - tt.height / 2;
          left = sr.left + sr.width + gap;
          break;
        default:
          top = sr.top + sr.height + gap;
          left = sr.left + sr.width / 2 - tt.width / 2;
      }

      // Clamp to viewport
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      if (left < 12) left = 12;
      if (left + tt.width > vw - 12) left = vw - tt.width - 12;
      if (top < 12) top = 12;
      if (top + tt.height > vh - 12) top = vh - tt.height - 12;

      setTooltipPos({ top, left, placement });
      if (!readyRef.current) {
        readyRef.current = true;
        setTimeout(() => setReady(true), 20);
      }
    };

    // Position immediately, then refine after scroll settles
    // Clear any previous refinement timer to prevent stale closures
    clearTimeout(measureTimer.current);
    requestAnimationFrame(positionTooltip);
    measureTimer.current = setTimeout(positionTooltip, 150);
  }, [step, centerTooltip]);

  useEffect(() => {
    measure();
  }, [current, measure]);

  // Recalculate on resize
  useEffect(() => {
    const handleResize = () => {
      clearTimeout(resizeTimer.current);
      resizeTimer.current = setTimeout(measure, 100);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimer.current);
    };
  }, [measure]);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        if (!isLast) goTo(c => c + 1);
        else onClose();
      }
      if (e.key === 'ArrowLeft' && !isFirst) goTo(c => c - 1);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isFirst, isLast, onClose]);

  if (!step) return null;

  // ── Arrow direction (points toward the target) ────────
  const arrowClass = tooltipPos
    ? step.placement === 'bottom' ? 'tour-arrow-top'
    : step.placement === 'top' ? 'tour-arrow-bottom'
    : step.placement === 'left' ? 'tour-arrow-right'
    : step.placement === 'right' ? 'tour-arrow-left'
    : ''
    : '';

  const arrowStyle = {};
  if (tooltipPos && spotlightRect) {
    const p = step.placement || 'bottom';
    if (p === 'bottom' || p === 'top') {
      arrowStyle.left = Math.max(24, Math.min(
        spotlightRect.left + spotlightRect.width / 2 - tooltipPos.left,
        340 - 24
      ));
    } else {
      arrowStyle.top = Math.max(24, Math.min(
        spotlightRect.top + spotlightRect.height / 2 - tooltipPos.top,
        300
      ));
    }
  }

  return (
    <div className="tour-overlay">
      {/* Backdrop (click = skip) */}
      <div
        className="tour-backdrop"
        onClick={onClose}
        style={!spotlightRect ? { background: 'rgba(0, 0, 0, 0.52)' } : undefined}
      />

      {/* Spotlight */}
      {spotlightRect && (
        <>
          <div
            className="tour-spotlight"
            style={{
              top: spotlightRect.top,
              left: spotlightRect.left,
              width: spotlightRect.width,
              height: spotlightRect.height,
            }}
          />
          <div
            className="tour-spotlight-pulse"
            style={{
              top: spotlightRect.top,
              left: spotlightRect.left,
              width: spotlightRect.width,
              height: spotlightRect.height,
            }}
          />
        </>
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className={`tour-tooltip ${ready ? 'tour-tooltip-ready' : 'tour-tooltip-init'}`}
        style={tooltipPos ? { top: tooltipPos.top, left: tooltipPos.left } : { top: '50%', left: '50%' }}
      >
        {/* Arrow */}
        {step.target && tooltipPos && arrowClass && (
          <div className={`tour-arrow ${arrowClass}`} style={arrowStyle} />
        )}

        <div className="tour-tooltip-content">
          <div className="tour-tree-badge">🌲</div>
          <div className="tour-tooltip-step">Step {current + 1} of {steps.length}</div>
          <h3 className="tour-tooltip-title">{step.title}</h3>
          <p className="tour-tooltip-body" dangerouslySetInnerHTML={{ __html: step.body }} />
        </div>

        <div className="tour-tooltip-footer">
          <div className="tour-tooltip-dots">
            {[0, 1, 2, 3, 4].map(i => {
              const progress = steps.length <= 1 ? 0 : (current / (steps.length - 1)) * 4;
              const distance = Math.abs(i - progress);
              const isActive = distance < 0.5;
              const scale = isActive ? 1.3 : distance < 1.5 ? 0.9 : distance < 2.5 ? 0.6 : 0.4;
              const opacity = isActive ? 1 : distance < 1.5 ? 0.6 : distance < 2.5 ? 0.35 : 0.2;
              return (
                <div
                  key={i}
                  className="tour-dot"
                  style={{
                    transform: `scale(${scale})`,
                    opacity,
                    background: isActive ? 'var(--teal)' : 'var(--ink-faint)',
                  }}
                />
              );
            })}
          </div>
          <div className="tour-tooltip-nav">
            <button className="tour-btn tour-btn-skip" onClick={onClose}>
              Skip
            </button>
            {!isFirst && (
              <button className="tour-btn tour-btn-back" onClick={() => goTo(c => c - 1)}>
                Back
              </button>
            )}
            {isLast ? (
              <button className="tour-btn tour-btn-finish" onClick={onClose}>
                Done
              </button>
            ) : (
              <button className="tour-btn tour-btn-next" onClick={() => goTo(c => c + 1)}>
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────────────────────────────────────────
   TOUR TRIGGER BUTTON — drop into any toolbar
   ─────────────────────────────────────────────────────────── */
export function TourTrigger({ onClick }) {
  return (
    <button
      className="btn-tour-trigger"
      onClick={onClick}
      title="Take a tour of the editor"
      aria-label="Take a tour"
    >
      <svg style={{ display: 'block' }} width="18" height="18" viewBox="6 5 12 15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><circle cx="12" cy="17" r="0.5" fill="currentColor"/>
      </svg>
    </button>
  );
}
